// src/pages/admin/memoirs/MemoirManagement.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  EditPencil,
  PlusIcon,
  EyeOpen,
  DeleteICon,
  FileIcon,
  FilterIcon,
  CloseIcon
} from '../../../public/icons';
import AdminLayout from '../layout';
import Pagination from '../../../component/shared/Pagination';
import CustomerMemoirDetailModal from '../../../component/admin/memior/CustomerMemoirDetailModal';
import CategoryDetailsModal from '../../../component/admin/memior/CategoryDetailsModal';
import AddCategoryModal from '../../../component/admin/memior/AddCategoryModal';
import AddQuestionsModal from '../../../component/admin/memior/AddQuestionsModal';
import ConfirmDialog from '../../../component/shared/ConfirmDialog';
import { useToast } from '../../../context/ToastContext';
import adminMemoirService from '../../../services/admin/adminMemoirService';

const MemoirManagement = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('memoirs');

  /* ── Filters ── */
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All categories');
  const [statusFilter, setStatusFilter] = useState('All status');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /* ── Categories state ── */
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  /* ── Memoirs state ── */
  const [memoirs, setMemoirs] = useState([]);
  const [loadingMemoirs, setLoadingMemoirs] = useState(false);
  const [memoirStats, setMemoirStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    inProgress: 0
  });
  const [memoirPagination, setMemoirPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  /* ── Modals ── */
  const [isMemoirModalOpen, setIsMemoirModalOpen] = useState(false);
  const [selectedMemoir, setSelectedMemoir] = useState(null);

  const [isCategoryDetailsModalOpen, setIsCategoryDetailsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isAddQuestionsModalOpen, setIsAddQuestionsModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  /* ── Confirm dialog ── */
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'danger',
    confirmText: 'Delete',
    onConfirm: null
  });

  /* ═══════════════════════════════════════════════════════
     LOADERS
     ═══════════════════════════════════════════════════════ */

  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await adminMemoirService.getCategories();
      const categoriesData = response.data?.categories || [];

      const formattedCategories = categoriesData.map((cat) => ({
        ...cat,
        questionsCount: cat.questions || 0,
        activeMemoirsCount: cat.activeMemoirsCount || 0,
        questions: []
      }));

      setCategories(formattedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      showError('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  }, [showError]);

  const loadMemoirs = useCallback(
    async (overrides = {}) => {
      try {
        setLoadingMemoirs(true);

        const statusMap = {
          'All status': undefined,
          'Active': 'active',
          'Completed': 'completed',
          'Pending': 'pending',
          'Processing': 'processing',
          'Cancelled': 'cancelled'
        };

        // Build a name → id map from the categories we already have
        const categoryMap = {};
        categories.forEach((c) => {
          categoryMap[c.name] = c._id;
        });

        const params = {
          page: memoirPagination.page || 1,
          limit: itemsPerPage || memoirPagination.limit || 10,
          search: searchQuery || undefined,
          status: statusMap[statusFilter] || undefined,
          category:
            categoryFilter !== 'All categories'
              ? categoryMap[categoryFilter]
              : undefined,
          ...overrides
        };

        const response = await adminMemoirService.getMemoirOrders(params);

        if (response.success) {
          const data = response.data || {};
          const list = data.memoirs || [];

          setMemoirs(list);
          setMemoirPagination(
            data.pagination || {
              page: 1,
              limit: params.limit,
              total: list.length,
              pages: 1
            }
          );

          // Prefer server-computed stats; fall back to page-local computation
          if (data.stats) {
            setMemoirStats({
              total: data.stats.total ?? list.length,
              active: data.stats.active ?? 0,
              completed: data.stats.completed ?? 0,
              inProgress: data.stats.inProgress ?? 0
            });
          } else {
            setMemoirStats({
              total: data.pagination?.total ?? list.length,
              active: list.filter((m) => m.statusRaw === 'active').length,
              completed: list.filter((m) => m.statusRaw === 'completed').length,
              inProgress: list.filter((m) =>
                ['pending', 'processing', 'active'].includes(m.statusRaw)
              ).length
            });
          }
        }
      } catch (error) {
        console.error('Error loading memoirs:', error);
        showError(
          error?.response?.data?.message || 'Failed to load memoirs'
        );
      } finally {
        setLoadingMemoirs(false);
      }
    },
    [
      categories,
      memoirPagination.page,
      memoirPagination.limit,
      itemsPerPage,
      searchQuery,
      statusFilter,
      categoryFilter,
      showError
    ]
  );

  /* ── Initial load ── */
  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Refetch memoirs when filters / page change ── */
  useEffect(() => {
    loadMemoirs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    categoryFilter,
    statusFilter,
    itemsPerPage,
    memoirPagination.page
  ]);

  /* ═══════════════════════════════════════════════════════
     MEMOIR HANDLERS
     ═══════════════════════════════════════════════════════ */

  const handleViewMemoir = async (memoir) => {
    setSelectedMemoir(memoir); // optimistic
    setIsMemoirModalOpen(true);

    try {
      const response = await adminMemoirService.getMemoirOrderById(memoir._id);
      if (response.success) setSelectedMemoir(response.data);
    } catch (error) {
      console.error('Failed to refresh memoir detail:', error);
      // Silent — optimistic data is still shown
    }
  };

  const handleCloseMemoirModal = () => {
    setIsMemoirModalOpen(false);
    setSelectedMemoir(null);
  };

  /* ═══════════════════════════════════════════════════════
     CATEGORY HANDLERS
     ═══════════════════════════════════════════════════════ */

  const handleViewCategory = async (category) => {
    try {
      setLoadingCategories(true);
      const response = await adminMemoirService.getCategoryById(category._id);
      setSelectedCategory(response.data);
      setIsCategoryDetailsModalOpen(true);
    } catch (error) {
      console.error('Error fetching category details:', error);
      showError('Failed to load category details');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryDetailsModalOpen(false);
    setSelectedCategory(null);
  };

  const handleAddCategory = () => {
    setCategoryToEdit(null);
    setIsAddCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setCategoryToEdit(category);
    setIsAddCategoryModalOpen(true);
  };

  const handleCloseAddCategoryModal = () => {
    setIsAddCategoryModalOpen(false);
    setCategoryToEdit(null);
    loadCategories();
  };

  const handleAddQuestions = () => {
    setIsAddQuestionsModalOpen(true);
  };

  const handleCloseAddQuestionsModal = () => {
    setIsAddQuestionsModalOpen(false);
    loadCategories();
  };

  const handleSubmitCategory = async () => {
    await loadCategories();
  };

  const handleSubmitQuestions = async () => {
    await loadCategories();
  };

  const handleDeleteCategory = (categoryId, categoryName) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Category',
      message: `Are you sure you want to delete "${categoryName}"? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await adminMemoirService.deleteCategory(categoryId);
          success('Category deleted successfully');
          await loadCategories();
          closeConfirmDialog();
        } catch (error) {
          console.error('Error deleting category:', error);
          showError(
            error.response?.data?.message || 'Failed to delete category'
          );
          closeConfirmDialog();
        }
      }
    });
  };

  const handleDeleteQuestion = (questionId, questionText) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Question',
      message: `Are you sure you want to delete this question? "${questionText.substring(
        0,
        50
      )}..."`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await adminMemoirService.deleteQuestion(questionId);
          success('Question deleted successfully');

          if (selectedCategory) {
            const response = await adminMemoirService.getCategoryById(
              selectedCategory._id
            );
            setSelectedCategory(response.data);
          }
          await loadCategories();
          closeConfirmDialog();
        } catch (error) {
          console.error('Error deleting question:', error);
          showError(
            error.response?.data?.message || 'Failed to delete question'
          );
          closeConfirmDialog();
        }
      }
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: null
    });
  };

  const handleEditQuestion = async (questionId, updatedQuestionText) => {
    try {
      await adminMemoirService.updateQuestion(questionId, {
        question: updatedQuestionText
      });
      success('Question updated successfully');

      if (selectedCategory) {
        const response = await adminMemoirService.getCategoryById(
          selectedCategory._id
        );
        setSelectedCategory(response.data);
      }

      await loadCategories();
    } catch (error) {
      console.error('Error updating question:', error);
      showError(
        error.response?.data?.message || 'Failed to update question'
      );
    }
  };

  /* ═══════════════════════════════════════════════════════
     RENDER HELPERS
     ═══════════════════════════════════════════════════════ */

  const getStatusBadge = (status, statusConfig) => {
    const cfg = statusConfig || {
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      dotColor: 'bg-gray-500'
    };
    return (
      <span
        className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs leading-5 font-semibold rounded-full ${cfg.bgColor} w-fit`}
      >
        <span
          className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${cfg.dotColor}`}
        />
        <span className={cfg.textColor}>{status}</span>
        <ArrowDown size={10} className={cfg.textColor} />
      </span>
    );
  };

  const getProgressBar = (progress, total) => {
    const safeTotal = total || 1;
    const percentage = Math.min(100, (progress / safeTotal) * 100);
    return (
      <div className="flex items-center gap-2">
        <div className="w-12 sm:w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#55000C] rounded-full"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs sm:text-sm text-[#0E1628]">
          {progress}/{safeTotal}
        </span>
      </div>
    );
  };

  /* ── Category options for the filter dropdown (built from live data) ── */
  const categoryOptions = useMemo(
    () => ['All categories', ...categories.map((c) => c.name)],
    [categories]
  );

  const statusOptions = [
    'All status',
    'Active',
    'Completed',
    'Pending',
    'Processing',
    'Cancelled'
  ];

  /* ── Client-side filter for the categories tab (memoirs are server-filtered) ── */
  const filteredCategories = categories.filter(
    (category) =>
      category.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /* ── Stats cards data ── */
  const statsCards = [
    {
      label: 'Total Memoirs',
      value: memoirStats.total,
      icon: '📚',
      color: 'bg-[#F4E7F1] text-[#8F2F84]'
    },
    {
      label: 'Active Memoirs',
      value: memoirStats.active,
      icon: '📖',
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Completed',
      value: memoirStats.completed,
      icon: '✅',
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    },
    {
      label: 'In Progress',
      value: memoirStats.inProgress,
      icon: '⏳',
      color: 'bg-[#E7F4ED] text-[#2F8F5B]'
    }
  ];

  /* ═══════════════════════════════════════════════════════
     MOBILE FILTERS MODAL
     ═══════════════════════════════════════════════════════ */
  const MobileFiltersModal = () => (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={() => setMobileFiltersOpen(false)}
      />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button
            onClick={() => setMobileFiltersOpen(false)}
            className="p-1"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1C2A46] mb-2">
              Search
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by customer name, book title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1C2A46] mb-2">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setMemoirPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full p-2.5 pl-3 pr-8 border border-[#DBDBDB] bg-transparent text-[#1C2A46] text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
            >
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1C2A46] mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setMemoirPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full p-2.5 pl-3 pr-8 border border-[#DBDBDB] bg-transparent text-[#1C2A46] text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={() => setMobileFiltersOpen(false)}
          className="w-full mt-6 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
        >
          Apply Filters
        </button>
      </div>
    </>
  );

  /* ═══════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════ */
  return (
    <AdminLayout>
      <div className="px-2 sm:px-4">
        {/* Stats Grid - memoirs tab only */}
        {activeTab === 'memoirs' && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            {statsCards.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm"
              >
                <div className="text-left">
                  <div
                    className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
                  >
                    <span className="text-base sm:text-xl">{stat.icon}</span>
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
                      {stat.value}
                    </p>
                    <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div>
          {/* Tabs + Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 md:mb-10">
            <div className="flex w-full sm:w-auto border-b sm:border-b-0">
              <button
                onClick={() => setActiveTab('memoirs')}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors relative ${
                  activeTab === 'memoirs'
                    ? 'text-[#55000C] border-b-2 border-[#55000C]'
                    : 'text-[#6B635D] hover:text-[#1C2A46]'
                }`}
              >
                Customer Memoirs
              </button>

              <button
                onClick={() => setActiveTab('categories')}
                className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors relative ${
                  activeTab === 'categories'
                    ? 'text-[#55000C] border-b-2 border-[#55000C]'
                    : 'text-[#6B635D] hover:text-[#1C2A46]'
                }`}
              >
                Categories & Questions
              </button>
            </div>

            {activeTab === 'categories' && (
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  onClick={handleAddCategory}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-white bg-[#D2A146] rounded-sm hover:bg-[#C49660] transition-colors"
                >
                  <PlusIcon size={16} color="#fff" />
                  <span>Add Category</span>
                </button>
                <button
                  onClick={handleAddQuestions}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-[#E4E4E4] rounded-sm hover:bg-gray-50 transition-colors"
                >
                  <PlusIcon size={16} color="#000" />
                  <span>Add Questions</span>
                </button>
              </div>
            )}
          </div>

          {/* Search + filters - memoirs tab only */}
          {activeTab === 'memoirs' && (
            <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB] bg-white rounded-sm shadow-sm">
              {/* Desktop */}
              <div className="hidden md:flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
                  All Memoirs
                </h2>

                <div className="flex gap-3 lg:gap-4 items-center">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <SearchIcon size={18} className="text-[#6B635D]" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search by customer name, book title..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setMemoirPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      className="p-2 pl-9 pr-3 border w-56 lg:w-72 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                    />
                  </div>

                  <div className="relative">
                    <select
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setMemoirPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      className="p-2 pl-3 pr-7 border w-36 lg:w-44 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                    >
                      {categoryOptions.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ArrowDown size={14} className="text-[#6B635D]" />
                    </div>
                  </div>

                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setMemoirPagination((prev) => ({ ...prev, page: 1 }));
                      }}
                      className="p-2 pl-3 pr-7 border w-32 lg:w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <ArrowDown size={14} className="text-[#6B635D]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile */}
              <div className="md:hidden">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-medium text-[#0E1628]">
                    All Memoirs
                  </h2>
                  <button
                    onClick={() => setMobileFiltersOpen(true)}
                    className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
                  >
                    <FilterIcon size={14} />
                    Filters
                  </button>
                </div>

                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <SearchIcon size={16} className="text-[#6B635D]" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by customer name, book title..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setMemoirPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tab content */}
          {activeTab === 'categories' ? (
            /* Categories grid */
            <div className="p-3 sm:p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {loadingCategories ? (
                <div className="col-span-full text-center py-8 sm:py-10 text-[#6B635D] text-sm">
                  Loading categories...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="col-span-full text-center py-8 sm:py-10 text-[#6B635D] text-sm">
                  No categories found
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <div
                    key={category._id}
                    className="bg-white p-4 sm:p-5 hover:shadow-md transition-shadow rounded"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="bg-[#FFEDF0] p-2 rounded-md">
                        <FileIcon size={20} color="#55000C" />
                      </div>
                      <div className="flex gap-1 sm:gap-2">
                        <button
                          onClick={() => handleEditCategory(category)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <EditPencil size={20} className="text-[#1E1E1E]" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteCategory(category._id, category.name)
                          }
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          <DeleteICon size={20} className="text-[#C03A2B]" />
                        </button>
                      </div>
                    </div>

                    <h3 className="text-base sm:text-lg font-medium text-[#1C2A46] mb-1 text-left">
                      {category.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-[#6B635D] text-left border-b-2 pb-3 mb-4 sm:mb-5 border-[#F5F5F5]">
                      {category.description}
                    </p>

                    <div className="flex justify-between items-center gap-4">
                      <div className="flex gap-3 sm:gap-5 items-center">
                        <div className="flex flex-col text-left">
                          <span className="text-sm sm:text-base font-bold text-[#0E1628]">
                            {category.questionsCount || 0}
                          </span>
                          <span className="text-xs sm:text-sm text-[#6B635D]">
                            Questions
                          </span>
                        </div>
                        <div className="flex flex-col text-left">
                          <span className="text-sm sm:text-base font-normal text-[#0E1628]">
                            {category.activeMemoirsCount || 0}
                          </span>
                          <span className="text-xs sm:text-sm text-[#6B635D]">
                            Active
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleViewCategory(category)}
                        className="text-left text-[#D4A574] hover:text-[#C49660] font-medium text-xs sm:text-sm flex items-center gap-1 group"
                      >
                        View
                        <span className="group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Memoirs table */
            <div className="overflow-x-auto">
              <table className="min-w-[700px] md:min-w-full divide-y divide-[#DBDBDB]">
                <thead className="bg-[#F3F4F6]">
                  <tr>
                    {[
                      'Customer',
                      'Memoir Title',
                      'Type',
                      'Delivery',
                      'Progress',
                      'Status',
                      'Action'
                    ].map((h) => (
                      <th
                        key={h}
                        className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider ${
                          h === 'Action' ? 'w-20' : ''
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#DBDBDB]">
                  {loadingMemoirs ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-10 text-sm text-[#6B635D]"
                      >
                        Loading memoirs…
                      </td>
                    </tr>
                  ) : memoirs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-10 text-sm text-[#6B635D]"
                      >
                        No memoirs found
                      </td>
                    </tr>
                  ) : (
                    memoirs.map((memoir) => (
                      <tr
                        key={memoir._id}
                        className="hover:bg-gray-50 transition-colors duration-150 text-left"
                      >
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                            {memoir.customer}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                          <span className="text-xs sm:text-sm font-normal text-[#0E1628] line-clamp-1">
                            {memoir.title}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className="text-xs sm:text-sm font-normal text-[#8A9099]">
                            {memoir.type}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <span className="text-xs sm:text-sm font-normal text-[#8A9099]">
                            {memoir.delivery}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {getProgressBar(memoir.progress, memoir.totalWeeks)}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          {getStatusBadge(memoir.status, memoir.statusConfig)}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewMemoir(memoir)}
                            className="text-xs sm:text-sm font-normal text-[#D2A146] hover:text-[#C49660] transition-colors flex items-center gap-1"
                          >
                            <EyeOpen size={14} />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination - memoirs tab only */}
          {activeTab === 'memoirs' && memoirPagination.pages > 1 && (
            <div className="mt-4 sm:mt-6">
              <Pagination
                currentPage={memoirPagination.page}
                totalPages={memoirPagination.pages}
                pageSize={itemsPerPage}
                totalItems={memoirPagination.total}
                onPageChange={(page) =>
                  setMemoirPagination((prev) => ({ ...prev, page }))
                }
                onPageSizeChange={(size) => {
                  setItemsPerPage(size);
                  setMemoirPagination((prev) => ({ ...prev, page: 1 }));
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {mobileFiltersOpen && <MobileFiltersModal />}

      {/* Customer Memoir Detail Modal */}
      <CustomerMemoirDetailModal
        isOpen={isMemoirModalOpen}
        onClose={handleCloseMemoirModal}
        memoir={selectedMemoir}
      />

      {/* Category Details Modal */}
      <CategoryDetailsModal
        isOpen={isCategoryDetailsModalOpen}
        onClose={handleCloseCategoryModal}
        category={selectedCategory}
        onDeleteQuestion={handleDeleteQuestion}
        onEditQuestion={handleEditQuestion}
      />

      {/* Add/Edit Category Modal */}
      <AddCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={handleCloseAddCategoryModal}
        onCategoryAdded={handleSubmitCategory}
        categoryToEdit={categoryToEdit}
      />

      {/* Add Questions Modal */}
      <AddQuestionsModal
        isOpen={isAddQuestionsModalOpen}
        onClose={handleCloseAddQuestionsModal}
        onQuestionsAdded={handleSubmitQuestions}
        categories={categories}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />
    </AdminLayout>
  );
};

export default MemoirManagement;